import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Hook that checks for upcoming billing cycle cutoffs and creates notifications.
 * Runs once on mount for logged-in users.
 */
export const useBillingCycleNotifications = () => {
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;

    const checkCutoffs = async () => {
      try {
        const today = new Date();
        const todayStr = today.toISOString().split("T")[0];

        const twoDaysOut = new Date(today);
        twoDaysOut.setDate(today.getDate() + 2);
        const twoDaysStr = twoDaysOut.toISOString().split("T")[0];

        const { data: cycles } = await supabase
          .from("billing_cycles")
          .select("*, projects(name)")
          .eq("status", "open")
          .or(`cutoff_date.eq.${todayStr},cutoff_date.eq.${twoDaysStr}`);

        if (!cycles?.length) return;

        const { data: companyId } = await supabase.rpc("my_company_id");
        if (!companyId) return;

        for (const cycle of cycles) {
          const isTwoDays = cycle.cutoff_date === twoDaysStr;
          const isToday = cycle.cutoff_date === todayStr;
          const projectName = (cycle as any).projects?.name ?? "Project";

          if (isTwoDays && !cycle.notifications_sent_2day) {
            await supabase.from("notifications").insert({
              company_id: companyId,
              entity_type: "billing_cycle",
              entity_id: cycle.id,
              entity_label: cycle.cycle_name,
              project_id: cycle.project_id,
              type: "billing_cutoff",
              title: `Billing cutoff in 2 days`,
              message: `${projectName} — cutoff is ${cycle.cutoff_date}`,
              priority: "normal",
            } as any);

            await supabase
              .from("billing_cycles")
              .update({ notifications_sent_2day: true } as any)
              .eq("id", cycle.id);
          }

          if (isToday && !cycle.notifications_sent_day_of) {
            await supabase.from("notifications").insert({
              company_id: companyId,
              entity_type: "billing_cycle",
              entity_id: cycle.id,
              entity_label: cycle.cycle_name,
              project_id: cycle.project_id,
              type: "billing_cutoff",
              title: `⚠️ Billing cutoff TODAY`,
              message: `${projectName} — submit by end of day`,
              priority: "high",
            } as any);

            await supabase
              .from("billing_cycles")
              .update({ notifications_sent_day_of: true } as any)
              .eq("id", cycle.id);
          }
        }
      } catch (err) {
        console.warn("Billing cycle notification check failed:", err);
      }
    };

    checkCutoffs();
  }, [user]);
};
